import { Checkbox, message, TableColumnsType } from 'antd';
import { Button, Footer, TitleBar } from '../../../../../components';
import TableV3 from '../../../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../../../utils';
import { useNavigate } from 'react-router';

import { ApolloError, useMutation, useQuery } from '@apollo/client';
import QueryErrorBoundary from '../../../../../components/error-handling';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { debounce } from 'lodash';
import { useState } from 'react';
import TitleUpdater from '../../../../../components/common/TitleUpdater';
import { BusinessFunction, BusinessFunctionDashboard } from './types';
import { GET_BUSINESS_FUNCTIONS_DASHBOARD } from './queries';
import { UPDATE_BUSINESS_FUNCTION } from '../../../../dataInputs/data-inputs/business-functions/queries';
import { StatusDisplay } from '../../../components/StatusDisplay';

dayjs.extend(relativeTime);

const BusinessFunctionsTagging = () => {
  const [checkedBusinessFunctions, setCheckedBusinessFunctions] = useState<{
    [key: string]: boolean;
  }>({});

  const navigate = useNavigate();

  const { data, loading, error } = useQuery<BusinessFunctionDashboard>(
    GET_BUSINESS_FUNCTIONS_DASHBOARD,
    {
      onCompleted: (data) => {
        const initialState: { [key: string]: boolean } = {};
        data?.getBusinessFunctionsDashboard.forEach((businessFunction) => {
          initialState[businessFunction.uuid] =
            businessFunction.supportedByITApplication;
        });
        setCheckedBusinessFunctions(initialState);
      },
    },
  );

  const [updateSupportAppDependency, { loading: updateCheckboxLoading }] =
    useMutation(UPDATE_BUSINESS_FUNCTION, {
      refetchQueries: ['GetBusinessFunctionsDashboard'],
    });

  const bizFunctionsDashboard = data?.getBusinessFunctionsDashboard ?? [];

  const loadingStates = loading || updateCheckboxLoading;

  const handleCheckboxToggle = async (e: CheckboxChangeEvent, uuid: string) => {
    const currentValue = e.target.checked;

    try {
      const response = await updateSupportAppDependency({
        variables: {
          data: {
            uuid,
            supportedByITApplication: currentValue,
          },
        },
      });

      if (response.data) {
        message.success('Changes Saved');
        setCheckedBusinessFunctions((prev) => ({
          ...prev,
          [uuid]: currentValue,
        }));
      }
    } catch (e) {
      if (e instanceof ApolloError) {
        message.error(e.message);
      }
    }
  };

  const debouncedHandleCheckbox = debounce(
    (e: CheckboxChangeEvent, uuid: string) => {
      handleCheckboxToggle(e, uuid);
    },
    300,
  );

  const columns: TableColumnsType<BusinessFunction> = [
    {
      title: 'Business Functions',
      width: 200,
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column ml-10">{text}</span>
      ),
    },
    {
      width: 120,
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.lastUpdated, b.lastUpdated),
      render: (dateString: string, record: BusinessFunction) => (
        <StatusDisplay isDirty={record.isDirty} lastUpdated={dateString} />
      ),
    },
    {
      title: '# of Applications Tagged',
      width: 120,
      dataIndex: 'applicationsTagged',
      key: 'applicationsTagged',
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.number(a.applicationsTagged, b.applicationsTagged),
      render: (text: number) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'No Application Dependency',
      width: 120,
      dataIndex: 'supportedByITApplication',
      key: 'supportedByITApplication',
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.boolean(
          a.supportedByITApplication,
          b.supportedByITApplication,
        ),
      render: (text: boolean, record) => {
        const { uuid, applicationsTagged } = record;

        const isChecked = checkedBusinessFunctions[uuid] && !applicationsTagged;

        return (
          <span className="custom-table--column">
            <Checkbox
              onChange={(checkedValue) => {
                if (checkedValue && applicationsTagged > 0) {
                  message.error('Business Function has IT applications mapped');
                  return;
                }
                debouncedHandleCheckbox(checkedValue, uuid);
              }}
              checked={text}
              defaultChecked={isChecked}
            />
          </span>
        );
      },
    },
  ];

  const extendOptions = (
    <Button
      type="tertiary"
      className="font-normal min-w-[280px]"
      onClick={() =>
        navigate(`/cyber/enterprise-level/business-functions/bulk`)
      }
    >
      Bulk Tagging
    </Button>
  );

  const extendActions = (record: BusinessFunction) => {
    const { uuid } = record;

    const isChecked = checkedBusinessFunctions[uuid];
    return !isChecked ? (
      <Button
        type="primary-rounded"
        className="w-full text-sm font-semibold min-w-[110px]"
        onClick={() =>
          navigate(
            `/cyber/enterprise-level/business-functions/${record.uuid}/it-application-selection`,
          )
        }
      >
        Tag & Describe Applications
      </Button>
    ) : null;
  };

  const enterpriseBusinessFunctionTaggingTitle =
    'Enterprise Business Function Tagging';

  return (
    <div>
      <TitleUpdater title={enterpriseBusinessFunctionTaggingTitle} />
      <TitleBar
        title={enterpriseBusinessFunctionTaggingTitle}
        subtitle="All Business Functions"
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          columns={columns}
          data={bizFunctionsDashboard}
          customStyles="nested-columns--custom"
          useBorderedSection
          loading={loadingStates}
          extendOptions={extendOptions}
          editable
          editableOptions={{
            update: false,
          }}
          extendActions={extendActions}
        />
      </QueryErrorBoundary>
      <Button
        type="primary"
        className="w-full text-sm font-bold max-w-[330px] mx-auto mt-7 py-4"
        onClick={() =>
          navigate('/cyber/enterprise-level/business-functions/selection')
        }
      >
        Select Business Functions
      </Button>
      <Footer backPath={'/cyber/enterprise-level'} />
    </div>
  );
};

export default BusinessFunctionsTagging;
